import React, { useState } from "react";
import API from "./adminAxios";
import { toast } from "react-toastify";
import { FaUserShield, FaKey, FaEnvelope, FaUser, FaUserPlus } from "react-icons/fa";

export default function CreateAdmin() {
  const [activeTab, setActiveTab] = useState("admin"); // 'admin' or 'user'

  // Admin Form State
  const [adminForm, setAdminForm] = useState({ username: "", email: "", password: "", registryKey: "" });
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  // User Form State (Now fully integrated with registryKey tracking)
  const [userForm, setUserForm] = useState({ username: "", email: "", password: "", registryKey: "" });
  const [loadingUser, setLoadingUser] = useState(false);

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (!adminForm.username || !adminForm.email || !adminForm.password || !adminForm.registryKey) {
      return toast.error("All fields, including the mandatory Registry Key, are required.");
    }
    try {
      setLoadingAdmin(true);
      const res = await API.post("/super-admin/create-admin", adminForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success(res.data.message || "Admin deployed successfully!");
      setAdminForm({ username: "", email: "", password: "", registryKey: "" });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to deploy admin");
    } finally {
      setLoadingAdmin(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!userForm.username || !userForm.email || !userForm.password) {
      return toast.error("Username, email, and password are required.");
    }
    try {
      setLoadingUser(true);
      const res = await API.post("/super-admin/create-user", userForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success(res.data.message || "User deployed successfully!");
      setUserForm({ username: "", email: "", password: "", registryKey: "" });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to deploy user");
    } finally {
      setLoadingUser(false);
    }
  };

  return (
    <div style={{ maxWidth: "650px", fontFamily: "'Inter', sans-serif" }}>
      <h2 style={{ color: "#fff", marginBottom: "8px", fontSize: "24px" }}>Staff & User Deployment</h2>
      <p style={{ color: "#94a3b8", marginBottom: "25px", fontSize: "14px" }}>Create isolated admin nodes or assign standard users directly to specific registry keys.</p>

      {/* Tab Switcher */}
      <div style={{ display: "flex", gap: "15px", marginBottom: "25px" }}>
        <button
          onClick={() => setActiveTab("admin")}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: "14px",
            border: activeTab === "admin" ? "1px solid #00f2ff" : "1px solid rgba(255,255,255,0.08)",
            background: activeTab === "admin" ? "rgba(0, 242, 255, 0.1)" : "rgba(30, 41, 59, 0.3)",
            color: activeTab === "admin" ? "#00f2ff" : "#94a3b8",
            fontWeight: "800",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
        >
          <FaUserShield /> DEPLOY ADMIN NODE
        </button>

        <button
          onClick={() => setActiveTab("user")}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: "14px",
            border: activeTab === "user" ? "1px solid #6366f1" : "1px solid rgba(255,255,255,0.08)",
            background: activeTab === "user" ? "rgba(99, 102, 241, 0.1)" : "rgba(30, 41, 59, 0.3)",
            color: activeTab === "user" ? "#6366f1" : "#94a3b8",
            fontWeight: "800",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
        >
          <FaUserPlus /> CREATE USER
        </button>
      </div>

      {/* Admin Form View */}
      {activeTab === "admin" && (
        <form onSubmit={handleAdminSubmit} style={formCardStyle}>
          <h3 style={{ color: "#fff", margin: "0 0 10px", fontSize: "18px" }}>Deploy Isolated Admin</h3>
          
          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaUser /> ADMIN USERNAME</label>
            <input type="text" placeholder="JohnAdmin" value={adminForm.username} onChange={e => setAdminForm({...adminForm, username: e.target.value})} style={inputStyle} />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaEnvelope /> EMAIL ADDRESS</label>
            <input type="email" placeholder="admin@store.com" value={adminForm.email} onChange={e => setAdminForm({...adminForm, email: e.target.value})} style={inputStyle} />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaUserShield /> PASSWORD</label>
            <input type="password" placeholder="••••••••" value={adminForm.password} onChange={e => setAdminForm({...adminForm, password: e.target.value})} style={inputStyle} />
          </div>

          <div style={inputGroupStyle}>
            <label style={{ ...labelStyle, color: "#00f2ff" }}><FaKey /> REGISTRY KEY (REQUIRED)</label>
            <input type="text" placeholder="e.g. REG_MARITIME01" value={adminForm.registryKey} onChange={e => setAdminForm({...adminForm, registryKey: e.target.value})} style={inputStyle} />
          </div>

          <button type="submit" disabled={loadingAdmin} style={adminBtnStyle}>
            {loadingAdmin ? "DEPLOYING NODE..." : "DEPLOY ADMIN NODE"}
          </button>
        </form>
      )}

      {/* User Form View */}
      {activeTab === "user" && (
        <form onSubmit={handleUserSubmit} style={formCardStyle}>
          <h3 style={{ color: "#fff", margin: "0 0 10px", fontSize: "18px" }}>Create Standard User</h3>

          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaUser /> USERNAME</label>
            <input type="text" placeholder="Unique ID" value={userForm.username} onChange={e => setUserForm({...userForm, username: e.target.value})} style={inputStyle} />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaEnvelope /> EMAIL ADDRESS</label>
            <input type="email" placeholder="user@domain.com" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} style={inputStyle} />
          </div>

          <div style={inputGroupStyle}>
            <label style={{ ...labelStyle, color: "#6366f1" }}><FaKey /> LINK TO REGISTRY KEY (OPTIONAL)</label>
            <input 
              type="text" 
              placeholder="Leave blank or enter admin key (e.g. AABBCC)" 
              value={userForm.registryKey} 
              onChange={e => setUserForm({...userForm, registryKey: e.target.value})} 
              style={inputStyle} 
            />
            <small style={{ color: "#64748b", fontSize: "11px" }}>Match the admin's key to isolate user data to that node.</small>
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}><FaUserShield /> SECURITY PASSWORD</label>
            <input type="password" placeholder="••••••••" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} style={inputStyle} />
          </div>

          <button type="submit" disabled={loadingUser} style={userBtnStyle}>
            {loadingUser ? "DEPLOYING USER..." : "FINALIZE USER DEPLOYMENT"}
          </button>
        </form>
      )}
    </div>
  );
}

const formCardStyle = { background: "rgba(30, 41, 59, 0.3)", padding: "30px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.05)", display: "flex", flexDirection: "column", gap: "20px" };
const inputGroupStyle = { display: "flex", flexDirection: "column", gap: "8px" };
const labelStyle = { fontSize: "11px", fontWeight: "700", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" };
const inputStyle = { padding: "14px", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#fff", outline: "none", fontSize: "15px" };
const adminBtnStyle = { padding: "16px", background: "linear-gradient(135deg, #00f2ff 0%, #0284c7 100%)", color: "#0f172a", border: "none", borderRadius: "12px", fontWeight: "900", cursor: "pointer", fontSize: "14px", marginTop: "10px" };
const userBtnStyle = { padding: "16px", background: "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "900", cursor: "pointer", fontSize: "14px", marginTop: "10px" };